import React, { useState, useEffect } from "react";
import { Button, Label, Spinner, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import axios from "axios";
import Swal from "sweetalert2";

/**
 * Agrega un color o una talla a un articulo de Nuevo Pedido: se elige del
 * catalogo o, si no existe, se escribe y se da de alta ahi mismo.
 * Con "Agregarlo tambien al articulo" queda en el articulo para los siguientes
 * pedidos; si no, solo se usa en este pedido.
 *
 * onAgregar({ _id, name }, alArticulo) recibe el registro ya existente.
 */
const URLS = { Color: process.env.REACT_APP_URL_COLORES, Talla: process.env.REACT_APP_URL_TALLAS };

const norm = (t) =>
  String(t || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase().replace(/\s+/g, " ").trim();

function AgregarColorTalla({ isOpen, toggle, tipo, catalogo = [], existentes = [], articulo, onAgregar }) {
  const [texto, setTexto] = useState("");
  const [elegido, setElegido] = useState(null);
  const [alArticulo, setAlArticulo] = useState(true);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTexto("");
    setElegido(null);
    setAlArticulo(true);
  }, [isOpen, tipo]);

  const yaEsta = new Set(existentes.map(String));
  const opciones = catalogo
    .filter((c) => c && c._id && !yaEsta.has(String(c._id)) && c.is_active !== "No")
    .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { numeric: true }));
  // Lo escrito que ya existe en el catalogo se usa en lugar de duplicarlo.
  const igual = !elegido && texto.trim() ? catalogo.find((c) => norm(c.name) === norm(texto)) : null;
  const nuevo = !elegido && !igual && texto.trim() ? texto.trim().toUpperCase() : "";
  const repetido = (elegido || igual) && yaEsta.has(String((elegido || igual)._id));

  const listo = async () => {
    let registro = elegido || igual;
    setGuardando(true);
    try {
      if (!registro) {
        const { data } = await axios.post(
          URLS[tipo],
          { name: nuevo },
          { headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } }
        );
        registro = data;
      }
      await onAgregar({ _id: registro._id, name: registro.name }, alArticulo, !elegido && !igual);
      toggle();
    } catch (error) {
      Swal.fire(
        `No se pudo agregar la ${tipo.toLowerCase()}`,
        (error.response && error.response.data && error.response.data.message) || "Intenta de nuevo",
        "error"
      );
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        Agregar {tipo === "Color" ? "color" : "talla"} {articulo ? `a ${articulo.codigo || ""}` : ""}
      </ModalHeader>
      <ModalBody>
        <Label>Elige del catálogo o escribe {tipo === "Color" ? "uno nuevo" : "una nueva"}</Label>
        <Autocomplete
          freeSolo
          size="small"
          options={opciones}
          getOptionLabel={(o) => (typeof o === "string" ? o : (o && o.name) || "")}
          inputValue={texto}
          onInputChange={(e, v) => {
            setTexto(v || "");
            setElegido(null);
          }}
          onChange={(e, v) => setElegido(v && typeof v === "object" ? v : null)}
          renderInput={(params) => <TextField {...params} variant="outlined" placeholder={tipo === "Color" ? "Ej. VERDE OBSCURO" : "Ej. 2EG"} />}
        />
        {nuevo ? (
          <small className="text-info d-block mt-1">
            No existe: se dará de alta "{nuevo}" en el catálogo de {tipo === "Color" ? "colores" : "tallas"}.
          </small>
        ) : null}
        {repetido ? <small className="text-danger d-block mt-1">Ya está en este artículo.</small> : null}
        <div className="mt-3">
          <label style={{ fontSize: 14, cursor: "pointer" }}>
            <input type="checkbox" checked={alArticulo} onChange={(e) => setAlArticulo(e.target.checked)} style={{ marginRight: 6 }} />
            Agregarlo también al artículo (para los siguientes pedidos)
          </label>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle} disabled={guardando}>
          Cancelar
        </Button>
        <Button color="success" onClick={listo} disabled={guardando || repetido || !(elegido || igual || nuevo)}>
          {guardando ? <Spinner size="sm" /> : <i className="fas fa-plus" />} Agregar
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default AgregarColorTalla;
